import React, { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useCardListStatus } from '@/hooks'
import { APIWeb } from '@/api/index'
import RefreshList from '@/components/RefreshList'
import { createAvatar } from '@dicebear/core'
import { identicon } from '@dicebear/collection'
import Ripple from '@/components/Ripple'

interface TransactionRecordsProps {
  cardId: number
}

export default React.memo(function TransactionRecords({ cardId }: TransactionRecordsProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getCardListStatus } = useCardListStatus()
  const [data, setData] = useState<CardRecordItem[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  const handleRecordClick = useCallback((record: CardRecordItem, avatarSvg: string) => {
    navigate('/card/card-history-detail', { state: { record: { ...record, avatarSvg } } })
  }, [navigate])

  const fetchAssetFlowList = useCallback(async (page: number, isAppend: boolean = false) => {
    if (loading || !cardId) return
    setLoading(true)
    try {
      const response = await APIWeb.getCardRecords({ card_id: cardId, page, page_size: pageSize })
      if (response?.list) {
        const newData = response.list
        if (isAppend) {
          setData(prevData => [...prevData, ...newData])
        } else {
          setData(newData)
        }
        if (newData.length < pageSize) {
          setHasMore(false)
        } else {
          setCurrentPage(page + 1)
          setHasMore(true)
        }
      } else {
        setHasMore(false)
      }
    } catch (error) {
      console.error('获取卡片记录失败:', error)
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }, [cardId, loading])

  // 当卡片ID变化时，重新加载数据
  useEffect(() => {
    if (cardId) {
      setData([])
      setCurrentPage(1)
      setHasMore(true)
      fetchAssetFlowList(1, false)
    }
  }, [cardId])

  const loadMore = useCallback(async () => {
    await fetchAssetFlowList(currentPage, true)
  }, [fetchAssetFlowList, currentPage])

  const onRefresh = useCallback(async () => {
    await fetchAssetFlowList(1, false)
  }, [fetchAssetFlowList])

  const renderCardRecordItem = useCallback((record: CardRecordItem, index: number) => {
    const avatarSeed = record.id || record.merch_name || `record-${index}`
    const avatarSvg = createAvatar(identicon, {
      seed: String(avatarSeed),
      size: 32,
      backgroundColor: ['#f0f0f0', '#e0e0e0', '#d0d0d0', '#c0c0c0'],
    }).toDataUri()

    return (
      <Ripple className='rounded-2'>
        <div className="flex items-center justify-between py-3 px-3.5" onClick={() => handleRecordClick(record, avatarSvg)}>
          <div className="flex w-8 h-8 bg-[#EAEEF6] rounded-full justify-center items-center mr-2 overflow-hidden flex-shrink-0">
            <img className="w-4 h-4" src={avatarSvg} alt="avatar" />
          </div>
          <div className='flex-1'>
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-900">
                {record.merch_name || record.biz_type_string || 'Transaction'}
              </div>
              <div className="font-semibold text-xs text-black">
                {record.business_change ? (record.business_change > 0 ? `+${record.business_change}` : record.business_change) : '-'}
              </div>
            </div>

            <div className='flex items-center justify-between text-right'>
              <div className="text-2.5 text-[#979797] mt-2">
                {record.transaction_at_string || record.created_at_string}
              </div>
              <div className="font-normal text-xs text-[#808080] mt-2 break-all">
                {getCardListStatus(record.txn_type || '')}
              </div>
            </div>
          </div>
        </div>
      </Ripple>
    )
  }, [getCardListStatus, handleRecordClick])

  return (
    <div className='w-full my-6'>
      <div className="flex items-center justify-between mb-4">
        <span className="text-3.5 text-[#1A1A1A]">{t('交易记录')}</span>
      </div>
      <div className="bg-white py-3 pb-6 border-[0.5px] border-solid border-[0.5px] border-[#E5E5E5] rounded-3xl">
        <div className="max-h-100 min-h-50 overflow-y-auto" style={{ touchAction: 'pan-y' }}>
          <RefreshList
            data={data}
            renderItem={renderCardRecordItem}
            keyExtractor={(item, index) => `${item.id}-${index}`}
            loading={loading}
            hasMore={hasMore}
            onRefresh={onRefresh}
            onLoadMore={loadMore}
            emptyText={t('暂无记录')}
            finishedText={t('没有更多数据了')}
            loadingText={t('加载中')}
          />
        </div>
      </div>
    </div>
  )
})
